"use client";

import { useState } from "react";
import { X, Flag, AlertTriangle, Info, Building } from "lucide-react";
import { errorToast, successToast } from "../../utils/toasters";

const reportReasons = [
  {
    id: "closed",
    title: "Business is closed",
    description: "This business is permanently closed",
    icon: AlertTriangle,
  },
  {
    id: "incorrect_info",
    title: "Incorrect information",
    description: "Address, hours, or other details are wrong",
    icon: Info,
  },
  {
    id: "not_real",
    title: "Not a real business",
    description: "This business doesn't exist",
    icon: Building,
  },
];

export default function BusinessReportModal({
  isOpen,
  onClose,
  onReport,
  businessName,
}) {
  const [selectedReason, setSelectedReason] = useState("");

  const handleSubmit = async () => {
    try {
      if (selectedReason) {
        await onReport(selectedReason);
        handleClose();
        successToast(
          `Thank you for reporting. We'll review this business listing.`
        );
      } else {
        throw new Error("Please select a reason");
      }
    } catch (error) {
      console.error("Error while reporting business: ", error);
      errorToast("Error while reporting business");
    }
  };

  const handleClose = () => {
    setSelectedReason("");
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md max-h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-2">
            <Flag className="h-5 w-5 text-red-500" />
            <h2 className="text-xl font-semibold">Report this business</h2>
          </div>
          <button
            onClick={handleClose}
            className="p-1 h-8 w-8 rounded-md hover:bg-gray-100 transition-colors flex items-center justify-center"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="space-y-4">
            <p className="text-sm text-gray-600 font-medium">
              What's the issue?
            </p>

            <div className="space-y-3">
              {reportReasons.map((reason) => {
                const IconComponent = reason.icon;

                return (
                  <label
                    key={reason.id}
                    className="flex items-start space-x-3 cursor-pointer p-3 rounded-lg transition-colors hover:bg-gray-50 border border-gray-200"
                  >
                    <input
                      type="radio"
                      name="reportReason"
                      value={reason.id}
                      checked={selectedReason === reason.id}
                      onChange={(e) => setSelectedReason(e.target.value)}
                      className="mt-1 h-4 w-4 border-gray-300 text-red-600 focus:ring-red-500"
                    />
                    <div className="flex-1">
                      <div className="flex items-center space-x-2">
                        <IconComponent className="h-4 w-4 text-gray-600" />
                        <span className="font-medium text-gray-900">
                          {reason.title}
                        </span>
                      </div>
                      <div className="text-sm text-gray-500 mt-1">
                        {reason.description}
                      </div>
                    </div>
                  </label>
                );
              })}
            </div>

            <div className="flex justify-end space-x-2 pt-4">
              <button
                onClick={handleClose}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={!selectedReason}
                className="px-4 py-2 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed bg-red-600 text-white hover:bg-red-700"
              >
                Report Business
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
